import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileCheck2,
  AlertCircle,
  RefreshCw,
  MailWarning,
  UserX,
  SpellCheck,
  CheckCircle2,
  Search,
  Building2,
  MailX,
  Edit3,
  Terminal,
  CheckCheck,
  Send,
  X,
  Sparkles,
  Loader2,
  Check,
  Database,
  Globe,
  ArrowLeft,
  AlertTriangle,
  Info
} from "lucide-react";
import "./style.css";

// Domínios conhecidos para detector inteligente de typos
const dominiosConhecidos = {
  "gmai.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmial.com": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "oulook.com": "outlook.com",
  "outlok.com": "outlook.com",
  "yaho.com": "yahoo.com",
  "yhaoo.com": "yahoo.com",
  "coom.br": "com.br",
  "con.br": "com.br"
};

// Base inicial realista de pendências
const pendenciasIniciais = [
  {
    id: 1,
    empresa: "Soluções Tecnológicas Alfa Ltda",
    cnpj: "14.234.567/0001-89",
    documento: "Contrato de Prestação de Serviços 2026",
    bloco: "Bloco 2: Assinatura Diretor Comercial",
    assinante: "Carlos Eduardo Mendes",
    papel: "Diretor Comercial",
    email: "carlos.mendes@gmai.com",
    tipoErro: "SINTAXE",
    diagnosticoCurto: "Erro de digitação (@gmai.com)",
    diagnosticoDetalhe:
      "Domínio com falta da letra 'l'. O servidor de destino rejeitou a resolução MX.",
    smtpCode: "550 5.1.2",
    messageId: "msg_9941a8bc_alfa_2026@mail.transacional.com",
    rawResponse:
      "550 5.1.2 Host or domain name not found. Name service error for name=gmai.com type=MX: Host not found.",
    dataTentativa: "Hoje às 14:32",
    status: "PENDENTE"
  },
  {
    id: 2,
    empresa: "Indústria Metalúrgica Beta S/A",
    cnpj: "33.987.123/0001-45",
    documento: "Termo de Confidencialidade (NDA)",
    bloco: "Bloco 1: Assinatura Jurídico",
    assinante: "Mariana Alcantara",
    papel: "Advogada Responsável",
    email: "mariana.juridico@beta-metal.com.br",
    tipoErro: "HARD_BOUNCE",
    diagnosticoCurto: "Caixa Inexistente (550)",
    diagnosticoDetalhe:
      "A conta de e-mail informada não existe no servidor corporativo da empresa.",
    smtpCode: "550 5.1.1",
    messageId: "msg_7729b9cd_beta_2026@mail.transacional.com",
    rawResponse:
      "550 5.1.1 <mariana.juridico@beta-metal.com.br>: Recipient address rejected: User unknown in virtual mailbox table.",
    dataTentativa: "Hoje às 12:15",
    status: "PENDENTE"
  },
  {
    id: 3,
    empresa: "Varejo Brasil Distribuidora Ltda",
    cnpj: "08.456.789/0002-12",
    documento: "Aditivo Contratual de Fornecimento",
    bloco: "Bloco 3: Assinatura Financeiro",
    assinante: "Roberto Silveira Santos",
    papel: "Gerente Financeiro",
    email: "roberto.financeiro@hotmial.com",
    tipoErro: "SINTAXE",
    diagnosticoCurto: "Erro no provedor (@hotmial.com)",
    diagnosticoDetalhe:
      "Erro de digitação clássico de provedor público.",
    smtpCode: "550 5.1.2",
    messageId: "msg_3312c1ef_varejo_2026@mail.transacional.com",
    rawResponse: "550 5.1.2 Domain not found for hotmial.com.",
    dataTentativa: "Hoje às 09:40",
    status: "PENDENTE"
  },
  {
    id: 4,
    empresa: "Construtora Horizonte Real Ltda",
    cnpj: "21.654.321/0001-90",
    documento: "Aprovação de Medição de Obra",
    bloco: "Bloco 2: Assinatura Engenheiro Fiscal",
    assinante: "Fernanda Cristina Luz",
    papel: "Engenheira Chefe",
    email: "fernanda.eng@construtorahorizonte.com.br",
    tipoErro: "SOFT_BOUNCE",
    diagnosticoCurto: "Caixa Postal Cheia (552)",
    diagnosticoDetalhe:
      "Servidor de destino recusou o recebimento porque a cota de disco do usuário foi excedida.",
    smtpCode: "552 5.2.2",
    messageId: "msg_5521a0aa_obra_2026@mail.transacional.com",
    rawResponse:
      "552 5.2.2 <fernanda.eng@construtorahorizonte.com.br>: Mailbox is full / quota exceeded.",
    dataTentativa: "Ontem às 17:05",
    status: "PENDENTE"
  }
];

const ErroEmail = () => {
  const navigate = useNavigate();

  // Estados principais
  const [pendencias, setPendencias] = useState(pendenciasIniciais);
  const [resolvidosHoje, setResolvidosHoje] = useState(0);
  const [termoBusca, setTermoBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("TODOS");

  // Estados do Modal Corrigir
  const [modalCorrigirAberto, setModalCorrigirAberto] = useState(false);
  const [itemEmCorrecao, setItemEmCorrecao] = useState(null);
  const [novoEmail, setNovoEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle"); // 'idle' | 'valid' | 'invalid'
  const [sugestaoTypo, setSugestaoTypo] = useState(null);
  const [loadingReenvio, setLoadingReenvio] = useState(false);

  // Estados do Modal Logs
  const [modalLogsAberto, setModalLogsAberto] = useState(false);
  const [itemEmLogs, setItemEmLogs] = useState(null);

  // Sistema de Toasts
  const [toasts, setToasts] = useState([]);

  const adicionarToast = useCallback((titulo, mensagem, tipo = "success") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, titulo, mensagem, tipo }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const removerToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Estatísticas computadas
  const stats = useMemo(() => {
    const itensPendentes = pendencias.filter((i) => i.status === "PENDENTE");
    const pendentesCount = itensPendentes.length;
    const hardBounces = itensPendentes.filter((i) => i.tipoErro === "HARD_BOUNCE").length;
    const errosSintaxe = itensPendentes.filter(
      (i) => i.tipoErro === "SINTAXE" || i.tipoErro === "DOMINIO"
    ).length;

    return {
      pendentes: pendentesCount,
      hardBounces,
      errosSintaxe,
      resolvidos: resolvidosHoje
    };
  }, [pendencias, resolvidosHoje]);

  // Sincroniza o número de pendências com o badge de notificações do Sidebar
  useEffect(() => {
    try {
      localStorage.setItem("erro_email_pendentes", String(stats.pendentes));
    } catch (_) {}
    window.dispatchEvent(
      new CustomEvent("erro-email:atualizado", {
        detail: { pendentes: stats.pendentes }
      })
    );
  }, [stats.pendentes]);

  // Lista filtrada para exibição (busca por e-mail, nome da empresa ou CNPJ)
  const listaFiltrada = useMemo(() => {
    const termo = termoBusca.toLowerCase().trim();
    const termoDigitos = termoBusca.replace(/\D/g, "");

    return pendencias.filter((item) => {
      if (item.status !== "PENDENTE") return false;

      const matchTipo = filtroTipo === "TODOS" || item.tipoErro === filtroTipo;

      if (!termo) return matchTipo;

      const matchEmail = item.email ? item.email.toLowerCase().includes(termo) : false;
      const matchEmpresa = item.empresa
        ? item.empresa.toLowerCase().includes(termo)
        : (item.razao_social?.toLowerCase().includes(termo) || item.nome_empresa?.toLowerCase().includes(termo) || false);

      const cnpjLimpo = String(item.cnpj || "").replace(/\D/g, "");
      const matchCnpj =
        (item.cnpj && item.cnpj.toLowerCase().includes(termo)) ||
        (termoDigitos.length > 0 && cnpjLimpo.includes(termoDigitos));

      const matchAssinante = item.assinante ? item.assinante.toLowerCase().includes(termo) : false;

      const matchBusca = matchEmail || matchEmpresa || matchCnpj || matchAssinante;

      return matchTipo && matchBusca;
    });
  }, [pendencias, termoBusca, filtroTipo]);

  // Detector inteligente de typo no e-mail
  const analisarEmail = (valor) => {
    setNovoEmail(valor);
    const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const partes = valor.trim().split("@");

    // Checagem de typo no domínio
    if (partes.length === 2) {
      const dominio = partes[1].toLowerCase();
      if (dominiosConhecidos[dominio]) {
        const emailCorrigido = `${partes[0]}@${dominiosConhecidos[dominio]}`;
        setSugestaoTypo(emailCorrigido);
      } else {
        setSugestaoTypo(null);
      }
    } else {
      setSugestaoTypo(null);
    }

    if (!valor.trim()) {
      setEmailStatus("idle");
      return;
    }

    if (regexEmail.test(valor.trim())) {
      setEmailStatus("valid");
    } else {
      setEmailStatus("invalid");
    }
  };

  const aplicarSugestao = () => {
    if (sugestaoTypo) {
      analisarEmail(sugestaoTypo);
      setSugestaoTypo(null);
    }
  };

  // Abrir Modal de Correção
  const abrirModalCorrigir = (item) => {
    setItemEmCorrecao(item);
    setNovoEmail("");
    setEmailStatus("idle");

    const partes = item.email.split("@");
    if (partes.length === 2 && dominiosConhecidos[partes[1].toLowerCase()]) {
      const dominioSugerido = dominiosConhecidos[partes[1].toLowerCase()];
      setSugestaoTypo(`${partes[0]}@${dominioSugerido}`);
    } else {
      setSugestaoTypo(null);
    }

    setModalCorrigirAberto(true);
  };

  const fecharModalCorrigir = () => {
    if (loadingReenvio) return;
    setModalCorrigirAberto(false);
    setItemEmCorrecao(null);
    setNovoEmail("");
    setSugestaoTypo(null);
  };

  // Executar reenvio com loading
  const executarReenvio = () => {
    if (emailStatus !== "valid" || !novoEmail.trim()) {
      return;
    }

    setLoadingReenvio(true);

    setTimeout(() => {
      if (itemEmCorrecao) {
        setPendencias((prev) =>
          prev.map((i) =>
            i.id === itemEmCorrecao.id
              ? { ...i, status: "RESOLVIDO", email: novoEmail.trim() }
              : i
          )
        );
        setResolvidosHoje((prev) => prev + 1);
      }

      setLoadingReenvio(false);
      const emailDisparado = novoEmail.trim();
      fecharModalCorrigir();

      adicionarToast(
        "E-mail corrigido com sucesso!",
        `Novo disparo enviado para ${emailDisparado}. Notificação do bloco ativa.`,
        "success"
      );
    }, 1200);
  };

  // Abrir Modal Logs
  const abrirModalLogs = (item) => {
    setItemEmLogs(item);
    setModalLogsAberto(true);
  };

  const fecharModalLogs = () => {
    setModalLogsAberto(false);
    setItemEmLogs(null);
  };

  // Ação de Recarregar Dados
  const recarregarDados = () => {
    adicionarToast(
      "Dados sincronizados",
      "A lista de pendências de e-mail está atualizada com o servidor.",
      "success"
    );
  };

  // Limpar Filtros
  const limparFiltros = () => {
    setTermoBusca("");
    setFiltroTipo("TODOS");
  };

  // Render do Badge de Diagnóstico
  const renderBadgeErro = (item) => {
    let classeBadge = "badge-diagnostico badge-bounce";
    let Icone = AlertCircle;

    if (item.tipoErro === "SINTAXE") {
      classeBadge = "badge-diagnostico badge-sintaxe";
      Icone = SpellCheck;
    } else if (item.tipoErro === "SOFT_BOUNCE") {
      classeBadge = "badge-diagnostico badge-soft";
      Icone = Database;
    } else if (item.tipoErro === "DOMINIO") {
      classeBadge = "badge-diagnostico badge-dominio";
      Icone = Globe;
    }

    return (
      <div className="diagnostico-col">
        <span className={classeBadge}>
          <Icone size={13} className="shrink-0" />
          <span>{item.diagnosticoCurto}</span>
        </span>
        <p className="diagnostico-detalhe">{item.diagnosticoDetalhe}</p>
      </div>
    );
  };

  return (
    <div className="erro-email-pagina-wrapper">
      {/* Cabeçalho Único Integrado ao Fundo Azul do Sistema */}
      <header className="erro-email-header-unico">
        <div className="header-unico-esquerda">
          <div className="header-icon-box">
            <FileCheck2 size={26} />
          </div>
          <div className="header-unico-textos">
            <div className="header-unico-titulo-linha">
              <h1>Erros de E-mail de Assinatura</h1>
              <span
                className={`badge-pendentes-counter ${
                  stats.pendentes > 0 ? "badge-pendentes-alerta" : "badge-pendentes-ok"
                }`}
              >
                <AlertCircle size={14} />
                <span>
                  {stats.pendentes} Pendente{stats.pendentes === 1 ? "" : "s"}
                </span>
              </span>
            </div>
            <p className="header-unico-subtitulo">
              Central de Notificações • Identifique falhas no envio para o próximo bloco de assinatura, corrija o e-mail cadastrado e reenvie com 1 clique.
            </p>
          </div>
        </div>

        <div className="header-unico-direita">
          <div className="status-badge-operacional-azul">
            <span className="status-bullet-pulse"></span>
            <span>Serviço de Disparo: Operacional</span>
          </div>

          <button
            type="button"
            onClick={recarregarDados}
            className="btn-header-atualizar"
            title="Atualizar lista de erros"
          >
            <RefreshCw size={14} />
            <span>Atualizar Lista</span>
          </button>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="btn-header-voltar"
            title="Voltar ao Painel Principal"
          >
            <ArrowLeft size={15} />
            <span>Voltar</span>
          </button>
        </div>
      </header>

      {/* Quick Stats Cards */}
      <section className="stats-cards-grid">
        <div className="stat-card">
          <div className="stat-card-text">
            <span className="stat-card-label">Pendentes de Ação</span>
            <p className="stat-card-valor text-rose">{stats.pendentes}</p>
          </div>
          <div className="stat-card-icon icon-rose">
            <MailWarning size={22} />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-text">
            <span className="stat-card-label">Endereço Inexistente</span>
            <p className="stat-card-valor">{stats.hardBounces}</p>
          </div>
          <div className="stat-card-icon icon-amber">
            <UserX size={22} />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-text">
            <span className="stat-card-label">Erros de Sintaxe / Domínio</span>
            <p className="stat-card-valor">{stats.errosSintaxe}</p>
          </div>
          <div className="stat-card-icon icon-indigo">
            <SpellCheck size={22} />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-text">
            <span className="stat-card-label">Reenviados Hoje</span>
            <p className="stat-card-valor text-emerald">{stats.resolvidos}</p>
          </div>
          <div className="stat-card-icon icon-emerald">
            <CheckCircle2 size={22} />
          </div>
        </div>
      </section>

      {/* Filters and Search Bar */}
      <section className="filtros-container-card">
        <div className="filtros-wrapper">
          <div className="search-input-wrapper">
            <Search size={17} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por empresa, CNPJ ou e-mail com falha..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              className="search-input"
            />
            {termoBusca && (
              <button
                type="button"
                onClick={() => setTermoBusca("")}
                className="btn-limpar-campo-busca"
                title="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="filtros-acoes">
            <div className="select-filtro-wrapper">
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                aria-label="Filtrar por tipo de falha"
                className="select-filtro"
              >
                <option value="TODOS">Todos os Tipos de Falha</option>
                <option value="HARD_BOUNCE">Caixa Inexistente (Hard Bounce)</option>
                <option value="SINTAXE">Erro de Sintaxe / Digitação</option>
                <option value="DOMINIO">Domínio Inexistente</option>
                <option value="SOFT_BOUNCE">Caixa Cheia (Soft Bounce)</option>
              </select>
            </div>

            {(termoBusca || filtroTipo !== "TODOS") && (
              <button
                type="button"
                onClick={limparFiltros}
                className="btn-limpar-filtros"
              >
                Limpar
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main Table Container */}
      <section className="tabela-erros-card">
        <div className="tabela-scroll-area">
          <table className="tabela-erros">
            <thead>
              <tr>
                <th>Empresa / CNPJ</th>
                <th>Documento / Bloco</th>
                <th>Assinante & E-mail com Erro</th>
                <th>Diagnóstico do Erro</th>
                <th className="th-center">Tentativa</th>
                <th className="th-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {listaFiltrada.map((item) => (
                <tr key={item.id} className="tabela-row">
                  {/* Empresa e CNPJ */}
                  <td className="td-empresa">
                    <div className="empresa-nome">{item.empresa}</div>
                    <div className="cnpj-badge-box">
                      <span className="cnpj-badge">
                        <Building2 size={13} className="cnpj-icon" />
                        {item.cnpj}
                      </span>
                    </div>
                  </td>

                  {/* Documento e Bloco */}
                  <td className="td-documento">
                    <span className="documento-titulo" title={item.documento}>
                      {item.documento}
                    </span>
                    <span className="bloco-badge">{item.bloco}</span>
                  </td>

                  {/* Assinante & E-mail com Erro */}
                  <td className="td-assinante">
                    <div className="assinante-info">
                      <strong>{item.assinante}</strong>
                      <span className="assinante-papel">({item.papel})</span>
                    </div>
                    <div className="email-erro-box">
                      <MailX size={14} className="email-erro-icon" />
                      <span className="email-erro-texto">{item.email}</span>
                    </div>
                  </td>

                  {/* Diagnóstico do Erro */}
                  <td className="td-diagnostico">{renderBadgeErro(item)}</td>

                  {/* Horário / Tentativa */}
                  <td className="td-tentativa">
                    <span className="tentativa-data">{item.dataTentativa}</span>
                    <span className="tentativa-origem">Via Provedor SMTP</span>
                  </td>

                  {/* Ações */}
                  <td className="td-acoes">
                    <button
                      type="button"
                      onClick={() => abrirModalCorrigir(item)}
                      className="btn-corrigir-reenviar"
                    >
                      <Edit3 size={14} />
                      <span>Corrigir & Reenviar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => abrirModalLogs(item)}
                      className="btn-ver-logs"
                    >
                      <Terminal size={13} />
                      <span>Ver Logs SMTP</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {listaFiltrada.length === 0 && (
          <div className="empty-state-card">
            <div className="empty-state-icon-circle">
              <CheckCheck size={32} />
            </div>
            <h3>Tudo limpo por aqui!</h3>
            <p>
              {termoBusca || filtroTipo !== "TODOS"
                ? "Nenhum e-mail com falha encontrado para o filtro pesquisado."
                : "Todos os e-mails com erro foram revisados e reenviados com sucesso."}
            </p>
            {(termoBusca || filtroTipo !== "TODOS") && (
              <button
                type="button"
                onClick={limparFiltros}
                className="btn-resetar-busca-empty"
              >
                Restaurar listagem
              </button>
            )}
          </div>
        )}
      </section>

      {/* MODAL: Corrigir e Reenviar */}
      {modalCorrigirAberto && itemEmCorrecao && (
        <div className="modal-backdrop">
          <div className="modal-dialog animate-modal">
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-brand">
                <div className="modal-icon-header">
                  <Send size={18} />
                </div>
                <div>
                  <h3>Corrigir e Reenviar E-mail</h3>
                  <p>Notificação do bloco de assinatura pendente</p>
                </div>
              </div>
              <button
                type="button"
                onClick={fecharModalCorrigir}
                className="btn-fechar-modal"
                disabled={loadingReenvio}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Card Resumo da Empresa */}
              <div className="modal-resumo-card">
                <div className="resumo-header">
                  <div>
                    <span className="resumo-rotulo">Empresa</span>
                    <strong className="resumo-empresa-nome">
                      {itemEmCorrecao.empresa}
                    </strong>
                  </div>
                  <div className="resumo-cnpj-col">
                    <span className="resumo-rotulo">CNPJ</span>
                    <span className="resumo-cnpj-badge">{itemEmCorrecao.cnpj}</span>
                  </div>
                </div>
                <div className="resumo-footer">
                  <span>
                    <strong>Assinante:</strong> {itemEmCorrecao.assinante} (
                    {itemEmCorrecao.papel})
                  </span>
                  <span className="resumo-bloco">{itemEmCorrecao.bloco}</span>
                </div>
              </div>

              {/* E-mail Atual com Falha */}
              <div className="email-atual-box">
                <label className="input-label-suave">E-mail Atual com Falha</label>
                <div className="email-atual-display">
                  <div className="email-atual-valor">
                    <MailX size={16} className="text-rose shrink-0" />
                    <span>{itemEmCorrecao.email}</span>
                  </div>
                  <span className="email-atual-badge">
                    {itemEmCorrecao.diagnosticoCurto}
                  </span>
                </div>
              </div>

              {/* Input Novo E-mail */}
              <div className="novo-email-form-group">
                <label htmlFor="novoEmailInput" className="input-label-destaque">
                  Novo E-mail para Disparo <span className="text-rose">*</span>
                </label>
                <div className="input-relative-wrapper">
                  <input
                    id="novoEmailInput"
                    type="email"
                    value={novoEmail}
                    onChange={(e) => analisarEmail(e.target.value)}
                    placeholder="ex: nome.sobrenome@empresa.com.br"
                    disabled={loadingReenvio}
                    className={`input-email-correcao ${
                      emailStatus === "valid"
                        ? "input-valid"
                        : emailStatus === "invalid"
                        ? "input-invalid"
                        : ""
                    }`}
                    autoFocus
                  />
                  <div className="input-feedback-icon">
                    {emailStatus === "valid" && (
                      <Check size={18} className="text-emerald" />
                    )}
                    {emailStatus === "invalid" && (
                      <AlertCircle size={18} className="text-rose" />
                    )}
                  </div>
                </div>

                {/* Sugestão Automática de Typo */}
                {sugestaoTypo && (
                  <div className="sugestao-typo-box">
                    <div className="sugestao-typo-texto">
                      <Sparkles size={16} className="text-amber shrink-0" />
                      <span>
                        Você quis dizer{" "}
                        <strong
                          onClick={aplicarSugestao}
                          className="sugestao-link"
                        >
                          {sugestaoTypo}
                        </strong>
                        ?
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={aplicarSugestao}
                      className="btn-aplicar-sugestao"
                    >
                      Usar sugestão
                    </button>
                  </div>
                )}

                <p
                  className={`feedback-msg-input ${
                    emailStatus === "valid"
                      ? "feedback-sucesso"
                      : emailStatus === "invalid"
                      ? "feedback-erro"
                      : ""
                  }`}
                >
                  {emailStatus === "valid"
                    ? "Formato de e-mail válido pronto para envio."
                    : emailStatus === "invalid"
                    ? "Por favor, informe um e-mail válido (ex: nome@dominio.com.br)."
                    : "Insira o endereço revisado para que o assinante receba o link da assinatura."}
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={fecharModalCorrigir}
                disabled={loadingReenvio}
                className="btn-modal-cancelar"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={executarReenvio}
                disabled={emailStatus !== "valid" || loadingReenvio}
                className="btn-modal-confirmar"
              >
                {loadingReenvio ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Disparando novo envio...</span>
                  </>
                ) : (
                  <>
                    <Send size={15} />
                    <span>Salvar e Disparar Novamente</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Detalhes Técnicos e Logs SMTP */}
      {modalLogsAberto && itemEmLogs && (
        <div className="modal-backdrop">
          <div className="modal-dialog modal-dialog-lg animate-modal">
            <div className="modal-header">
              <div className="modal-header-brand">
                <div className="modal-icon-header icon-header-terminal">
                  <Terminal size={18} />
                </div>
                <div>
                  <h3>Log Técnico do Provedor de E-mail</h3>
                  <p>Dados do evento de Bounce / Rejeição</p>
                </div>
              </div>
              <button
                type="button"
                onClick={fecharModalLogs}
                className="btn-fechar-modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body space-y-logs">
              <div className="logs-grid-duplo">
                <div className="log-mini-card">
                  <span className="log-rotulo">Código SMTP</span>
                  <strong className="log-valor-mono">{itemEmLogs.smtpCode}</strong>
                </div>
                <div className="log-mini-card">
                  <span className="log-rotulo">Status do Evento</span>
                  <strong className="log-valor-mono text-rose">
                    {itemEmLogs.diagnosticoCurto}
                  </strong>
                </div>
              </div>

              <div>
                <span className="log-rotulo-secao">
                  Message-ID (Provedor Transacional)
                </span>
                <div className="log-code-box">{itemEmLogs.messageId}</div>
              </div>

              <div>
                <span className="log-rotulo-secao">
                  Raw Response do Servidor de Destino (MX)
                </span>
                <pre className="terminal-pre-box">{itemEmLogs.rawResponse}</pre>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={fecharModalLogs}
                className="btn-modal-fechar-logs"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts Container */}
      <div className="toasts-container">
        {toasts.map((toast) => {
          const isSuccess = toast.tipo === "success";
          const isInfo = toast.tipo === "info";
          const IconeToast = isSuccess
            ? CheckCircle2
            : isInfo
            ? Info
            : AlertTriangle;

          return (
            <div
              key={toast.id}
              className={`toast-card ${
                isSuccess
                  ? "toast-success"
                  : isInfo
                  ? "toast-info"
                  : "toast-error"
              }`}
              onClick={() => removerToast(toast.id)}
            >
              <IconeToast size={20} className="toast-icon shrink-0" />
              <div className="toast-conteudo">
                <h4 className="toast-titulo">{toast.titulo}</h4>
                <p className="toast-mensagem">{toast.mensagem}</p>
              </div>
              <button
                type="button"
                className="toast-btn-fechar"
                onClick={(e) => {
                  e.stopPropagation();
                  removerToast(toast.id);
                }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ErroEmail;
